import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Chip } from '@weareopenr/component-library';
import { useBillingProfile, useInvoice, useInvoiceMutations } from '../../hooks';
import { isMockMode } from '../../constants';
import type { IBillingProfile, IInvoice } from '../../types';
import { fmtMoney, shortDate, statusColour, statusLabel } from '../Invoices/format';
import styles from './InvoiceView.module.scss';

const Address = ({ name, address }: { name: string; address: string }) => (
  <>
    <div>{name}</div>
    {address
      .split('\n')
      .filter(Boolean)
      .map((line) => (
        <div key={line}>{line}</div>
      ))}
  </>
);

/** The invoice as a page, mirroring the PDF: the "view in browser" the finance team asked for. */
export const InvoiceSheet = ({
  invoice,
  profile,
}: {
  invoice: IInvoice;
  profile: IBillingProfile;
}) => (
  <article className={styles.sheet} aria-label={`Invoice ${invoice.number}`}>
    <header className={styles.top}>
      <div className={styles.brand}>{profile.company_name}</div>
      <div className={styles.company}>
        <div>{profile.company_name}</div>
        {profile.address_lines.map((line) => (
          <div key={line}>{line}</div>
        ))}
        {profile.phone ? <div className={styles.phone}>Telephone : {profile.phone}</div> : null}
      </div>
      <div className={styles.meta}>
        <div className={styles.docType}>INVOICE</div>
        <dl>
          <dt>Invoice Date</dt>
          <dd>{shortDate(invoice.issued_on)}</dd>
          <dt>Due Date</dt>
          <dd>{shortDate(invoice.due_on)}</dd>
          <dt>Invoice No</dt>
          <dd>{invoice.number}</dd>
          <dt>Account No</dt>
          <dd>{invoice.account_number ?? ''}</dd>
        </dl>
      </div>
    </header>
    <section className={styles.addresses}>
      <div>
        <strong>Billing Address:</strong>
        <Address name={invoice.billing_name} address={invoice.billing_address} />
      </div>
      <div>
        <strong>Customer Address:</strong>
        <Address name={invoice.billing_name} address={invoice.customer_address} />
      </div>
    </section>
    <section className={styles.body}>
      <table className={styles.lines}>
        <thead>
          <tr>
            <th>Item</th>
            <th>Date</th>
            <th className={styles.desc}>Description</th>
            <th className={styles.num}>Qty</th>
            <th className={styles.num}>Unit</th>
            <th className={styles.num}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line, index) => (
            <tr key={`${line.description}-${index}`}>
              <td>{String(index + 1).padStart(3, '0')}</td>
              <td>{shortDate(invoice.issued_on)}</td>
              <td className={styles.desc}>{line.description}</td>
              <td className={styles.num}>{line.quantity.toLocaleString('en-GB')}</td>
              <td className={styles.num}>{line.unit_price.toFixed(2)}</td>
              <td className={styles.num}>{line.amount.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {invoice.notes ? <p className={styles.notes}>{invoice.notes}</p> : null}
      <div className={styles.totals}>
        <div>
          <span>Total Amount Invoiced:</span>
          <span>£</span>
          <span>{invoice.subtotal.toFixed(2)}</span>
        </div>
        <div>
          <span>Tax Amount ({invoice.vat_rate}%):</span>
          <span>£</span>
          <span>{invoice.vat_amount.toFixed(2)}</span>
        </div>
        <div>
          <span>Balance Due:</span>
          <span>£</span>
          <span>{invoice.total.toFixed(2)}</span>
        </div>
      </div>
      {profile.late_payment_note ? (
        <p className={styles.late}>{profile.late_payment_note}</p>
      ) : null}
      <p className={styles.bank}>
        {profile.bank_name ? (
          <>
            <strong>Bank Details:</strong> {profile.bank_name}{' '}
          </>
        ) : null}
        {profile.sort_code ? (
          <>
            <strong>Sort Code:</strong> {profile.sort_code}{' '}
          </>
        ) : null}
        {profile.account_number ? (
          <>
            <strong>A/C:</strong> {profile.account_number}{' '}
          </>
        ) : null}
        {profile.iban ? (
          <>
            <strong>IBAN:</strong> {profile.iban}{' '}
          </>
        ) : null}
        {profile.swift ? (
          <>
            <strong>SWIFT:</strong> {profile.swift}
          </>
        ) : null}
      </p>
    </section>
    <footer className={styles.foot}>
      <div>PAYMENT TERMS : Net {invoice.payment_terms_days} Days</div>
      <div>VAT No: {profile.vat_number ?? ''}</div>
      <div className={styles.registered}>
        {profile.registered_office ? `Registered Office: ${profile.registered_office}` : ''}
        {profile.company_number ? `   Company No: ${profile.company_number}` : ''}
      </div>
    </footer>
    <div className={styles.cut} aria-hidden="true" />
    <section className={styles.remittance}>
      <div>
        <strong>Please ensure all remittance is addressed to</strong>
        <div className={styles.remitBrand}>
          <span className={styles.brandSmall}>{profile.company_name}</span>
          <div className={styles.small}>
            <div>{profile.company_name}</div>
            {profile.address_lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        </div>
      </div>
      <div>
        <strong>Remittance From:</strong>
        <div className={styles.small}>
          <Address name={invoice.billing_name} address={invoice.billing_address} />
        </div>
      </div>
      <div>
        <strong>REMITTANCE</strong>
        <dl className={styles.slip}>
          <dt>Invoice Date</dt>
          <dd>{shortDate(invoice.issued_on)}</dd>
          <dt>Due Date</dt>
          <dd>{shortDate(invoice.due_on)}</dd>
          <dt>Invoice No</dt>
          <dd>{invoice.number}</dd>
          <dt>Account No</dt>
          <dd>{invoice.account_number ?? ''}</dd>
          <dt>Amount Due</dt>
          <dd>{fmtMoney(invoice.total, invoice.currency)}</dd>
          <dt className={styles.gap}>Bank Details</dt>
          <dd className={styles.gap}>{profile.bank_name ?? ''}</dd>
          <dt>Sort Code</dt>
          <dd>{profile.sort_code ?? ''}</dd>
          <dt>Account Num</dt>
          <dd>{profile.account_number ?? ''}</dd>
          <dt>IBAN</dt>
          <dd>{profile.iban ?? ''}</dd>
          <dt>SWIFT</dt>
          <dd>{profile.swift ?? ''}</dd>
        </dl>
      </div>
    </section>
  </article>
);

export const InvoiceView = () => {
  const { tenantId = '', invoiceId = '' } = useParams();
  const [params] = useSearchParams();
  const { data: invoice, isLoading, error } = useInvoice(tenantId, invoiceId);
  const { data: profile } = useBillingProfile();
  const { pdf } = useInvoiceMutations(tenantId);

  useEffect(() => {
    if (params.get('print') === '1' && invoice && profile) {
      window.print();
    }
  }, [params, invoice, profile]);

  const download = () =>
    pdf.mutate(
      { id: invoiceId, download: true },
      { onSuccess: ({ data }) => window.open(data.url, '_blank', 'noopener') },
    );

  if (isLoading) return <p className={styles.muted}>Loading invoice…</p>;
  if (error || !invoice || !profile) {
    return (
      <p className={styles.error}>
        Could not load: {(error as { message?: string })?.message ?? 'unknown error'}
      </p>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <nav className={styles.crumbs} aria-label="Breadcrumb">
          <Link to="/tenants">Tenants</Link>
          <span aria-hidden="true">/</span>
          <Link to={`/tenants/${encodeURIComponent(tenantId)}/invoices`}>
            {invoice.billing_name}
          </Link>
          <span aria-hidden="true">/</span>
          <span>Invoice {invoice.number}</span>
        </nav>
        <div className={styles.toolbarRight}>
          <Chip label={statusLabel(invoice)} backgroundColour={statusColour(invoice)} />
          {invoice.status === 'draft' ? (
            <span className={styles.draftNote}>Draft: not yet issued, figures may change.</span>
          ) : null}
          <button type="button" className={styles.ghost} onClick={() => window.print()}>
            Print
          </button>
          <button
            type="button"
            className={styles.primary}
            onClick={download}
            disabled={pdf.isLoading || isMockMode()}
            title={isMockMode() ? 'PDF download needs the real API' : undefined}
          >
            {pdf.isLoading ? 'Preparing…' : 'Download PDF'}
          </button>
        </div>
      </div>
      <InvoiceSheet invoice={invoice} profile={profile} />
    </div>
  );
};
